import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { OverflowTabs, type Tab, type OverflowTabsRef } from "~/components/common/OverflowTabs";
import { DealFeed } from "~/components/common/DealFeed";
import { DealCard } from "~/components/common/DealCard";
import { RegularDealsStoreProvider, useRegularDealsStore } from "~/lib/stores/regular-deals";
import { Card, CardContent } from "~/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "~/components/ui/carousel";
import PageContainer from "~/components/common/PageContainer";
import Footer from "~/components/common/Footer";
import { devLog } from "~/lib/dev-log";

// Tab configuration with entrypoints
const TAB_CONFIG: { id: string; label: string; entrypoint: string }[] = [
  { id: "for_you", label: "For You", entrypoint: "popular" },
  { id: "grocery", label: "Grocery", entrypoint: "gc_grocery" },
  { id: "super-sale", label: "Super Sale", entrypoint: "subsidy_deals" },
  { id: "clearance", label: "Clearance", entrypoint: "high_discount" },
  { id: "branded", label: "Brands", entrypoint: "branded" },
  { id: "hot_sale", label: "Hot", entrypoint: "hot_sale" },
  { id: "fast_delivery", label: "Fast Delivery", entrypoint: "fast_delivery" },
  {
    id: "food_beverages",
    label: "Food & Beverages",
    entrypoint: "Rm9vZCwgQmV2ZXJhZ2VzICYgVG9iYWNjbw==",
  },
  { id: "media", label: "Media", entrypoint: "TWVkaWE=" },
  { id: "health_beauty", label: "Health & Beauty", entrypoint: "SGVhbHRoICYgQmVhdXR5" },
  { id: "baby_toddler", label: "Baby & Toddler", entrypoint: "QmFieSAmIFRvZGRsZXI=" },
  { id: "apparel_accessories", label: "Apparel & Accessories", entrypoint: "QXBwYXJlbCAmIEFjY2Vzc29yaWVz" },
  { id: "home_garden", label: "Home & Garden", entrypoint: "SG9tZSAmIEdhcmRlbg==" },
  { id: "toys_games", label: "Toys & Games", entrypoint: "VG95cyAmIEdhbWVz" },
  { id: "furniture", label: "Furniture", entrypoint: "RnVybml0dXJl" },
  { id: "electronics", label: "Electronics", entrypoint: "RWxlY3Ryb25pY3M=" },
  { id: "office_supplies", label: "Office Supplies", entrypoint: "T2ZmaWNlIFN1cHBsaWVz" },
  { id: "hardware", label: "Hardware", entrypoint: "SGFyZHdhcmU=" },
  { id: "luggage_bags", label: "Luggage & Bags", entrypoint: "THVnZ2FnZSAmIEJhZ3M=" },
  { id: "business_industrial", label: "Business & Industrial", entrypoint: "QnVzaW5lc3MgJiBJbmR1c3RyaWFs" },
  { id: "animals_pet_supplies", label: "Animals & Pet Supplies", entrypoint: "QW5pbWFscyAmIFBldCBTdXBwbGllcw==" },
  { id: "cameras_optics", label: "Cameras & Optics", entrypoint: "Q2FtZXJhcyAmIE9wdGljcw==" },
  { id: "sporting_goods", label: "Sporting Goods", entrypoint: "U3BvcnRpbmcgR29vZHM=" },
  { id: "arts_entertainment", label: "Arts & Entertainment", entrypoint: "QXJ0cyAmIEVudGVydGFpbm1lbnQ=" },
  { id: "vehicles_parts", label: "Vehicles & Parts", entrypoint: "VmVoaWNsZXMgJiBQYXJ0cw==" },
  { id: "software", label: "Software", entrypoint: "U29mdHdhcmU=" },
  { id: "religious_ceremonial", label: "Religious & Ceremonial", entrypoint: "UmVsaWdpb3VzICYgQ2VyZW1vbmlhbA==" },
];

// Fixed tabs for OverflowTabs
export const FIXED_TABS: Tab[] = TAB_CONFIG.map(({ id, label }) => ({ id, label }));

// Component for each tab content that uses the store
interface TabContentProps {
  tabId: string;
  isActive: boolean;
  showTopPicks?: boolean;
  showRecommend?: boolean;
  showTitle?: boolean;
}

export function TabContent({
  tabId,
  isActive,
  showTopPicks = true,
  showRecommend = true,
  showTitle = true,
}: TabContentProps) {
  const { deals, isLoading, fetchDeals, fetchMoreDeals, hasMore } = useRegularDealsStore();

  // Fetch deals only when tab becomes active and no deals are loaded yet
  useEffect(() => {
    if (isActive && deals.length === 0 && !isLoading) {
      const config = TAB_CONFIG.find(tab => tab.id === tabId);
      const entryPoint = config ? config.entrypoint : tabId;
      fetchDeals(entryPoint);
    }
  }, [isActive, deals.length, isLoading, fetchDeals, tabId]);

  // Handle load more deals
  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      fetchMoreDeals();
    }
  };

  // Split deals into Best sell (first 10) and Recommend (rest)
  const topPickDeals = showTopPicks ? deals.slice(0, 10) : [];
  const recommendDeals = showRecommend ? deals.slice(10) : [];

  return (
    <div className="space-y-10">
      {/* Top picks Section */}
      {topPickDeals.length > 0 && (
        <section>
          {showTitle && <h2 className="mb-4 text-3xl font-bold tracking-tight">Top picks</h2>}
          <div className="group">
            <Carousel
              opts={{
                align: "start",
                loop: false,
                dragFree: true,
                containScroll: "trimSnaps",
                duration: 20,
              }}
              className="relative w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {topPickDeals.map((deal, index) => (
                  <CarouselItem
                    key={deal._id}
                    className="basis-1/2 pl-2 md:basis-1/3 md:pl-4 lg:basis-1/4 xl:basis-1/5"
                  >
                    <DealCard
                      deal={deal}
                      badgeText={`No.${index + 1}`}
                      onClick={() => {
                        // open in new tab
                        window.open(`/deal/detail?deal_id=${deal._id}`, "_blank");
                      }}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious
                variant="carouselNav"
                size="icon"
                customPosition={true}
                className="absolute top-1/3 -left-4 z-10 inline-flex size-8 -translate-y-1/2 disabled:hidden"
              />
              <CarouselNext
                variant="carouselNav"
                size="icon"
                customPosition={true}
                className="absolute top-1/3 -right-4 z-10 inline-flex size-8 -translate-y-1/2 disabled:hidden"
              />
            </Carousel>
          </div>
        </section>
      )}

      {/* Recommend Section */}
      <section>
        {showTitle && <h2 className="mb-4 text-3xl font-bold tracking-tight">Recommended</h2>}
        <DealFeed deals={recommendDeals} onLoadMore={handleLoadMore} isLoading={isLoading} hasMore={hasMore} />
      </section>
    </div>
  );
}

export default function DealFeeds() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    return tabParam && FIXED_TABS.some(tab => tab.id === tabParam) ? tabParam : FIXED_TABS[0].id;
  });

  // Ref for the OverflowTabs component to enable scrolling
  const overflowTabsRef = useRef<OverflowTabsRef>(null);

  const handleTabChange = (tabId: string) => {
    devLog("Tab clicked:", tabId);
    setActiveTab(tabId);
    // update URL search param
    setSearchParams(prevParams => {
      const params = new URLSearchParams(prevParams);
      params.set("tab", tabId);
      return params;
    });
  };

  return (
    <PageContainer>
      <div className="container mx-auto p-4 pt-10">
        <Card>
          <CardContent>
            {/* Tabs */}
            <OverflowTabs
              ref={overflowTabsRef}
              tabs={FIXED_TABS}
              activeTabId={activeTab}
              onTabChange={handleTabChange}
              className="mb-6"
            />

            <RegularDealsStoreProvider channelId={activeTab}>
              <TabContent tabId={activeTab} isActive={true} />
            </RegularDealsStoreProvider>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </PageContainer>
  );
}
